import { DateTime } from 'luxon';
import { match } from 'ts-pattern';
import {
  FLEXIBLE_SIDES_DEFAULT_SIDE_PRICE,
  FLEXIBLE_SIDES_DEFAULT_STAPLE_PRICE,
  HST_TO_UTC_OFFSET,
  MEAL_PLAN_DEFAULT_SIDE_PRICE,
  MEAL_PLAN_REGIONS_IDS,
} from './Constants';

export const NUM_EXPECTED_FUTURE_ORDERS = 8;

type FoodItem = {
  isMainsPlusSides?: boolean | null;
  isFlexibleSides?: boolean | null;
  mealPlanFoodType?: string | null;
};

export const isDateInPausePeriod = (dateToCheck: Date, pauseEndDate: Date | null): boolean => {
  if (!pauseEndDate) {
    return false;
  }

  return DateTime.fromJSDate(dateToCheck) <= DateTime.fromJSDate(pauseEndDate);
};

export const isMealPlanPaused = (pauseEndDate: Date | null): boolean => isDateInPausePeriod(new Date(), pauseEndDate);

/**
 * Helper function to get a date object a specific number of days prior to the given delivery date
 *
 * @param dateString - delivery date string
 * @param daysToAdjust - number of days prior to delivery date to set
 * @param numAdditionalHoursToSubtract - if we should subtract addional hours from the date
 * @returns
 */
export const getPriorDateAt3pm = (
  dateString: string,
  daysToAdjust: number,
  numAdditionalHoursToSubtract: number
): Date => {
  const parsedDate = DateTime.fromISO(dateString);
  const adjustedDate = parsedDate.minus({ days: daysToAdjust });

  // Set time to 3pm (15:00) and adjust by additional hours if needed
  const finalDateTime = adjustedDate.set({
    hour: 15 - numAdditionalHoursToSubtract,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  return finalDateTime.toJSDate();
};

/**
 * Cutoff time for making adjustments to deliveries is 3pm 3 days prior to nextDeliveryDate
 * If the user visits the site after 3pm, we consider `currentDate` as the next day
 * To ensure that we don't run into issues with date comparisons, we set current time to 2pm
 * and set cutoffDate to 3pm to make sure we don't run into any edge cases
 */
export const getCutoffDate = (
  nextDeliveryDateString: string,
  numAdditionalHoursToSubtract = 0
): { isPastCutoffDate: boolean; cutoffDate: Date } => {
  const currentDate = new Date();
  const isAfter3pm = currentDate.getHours() >= 15;
  if (isAfter3pm) {
    // If it's after 3pm, set the date to the next day
    currentDate.setDate(currentDate.getDate() + 1);
    // set the hours to 2pm for the next day to compare to cutoff date
    currentDate.setHours(14, 0, 0, 0);
  }
  const cutoffDate = getPriorDateAt3pm(nextDeliveryDateString, 3, numAdditionalHoursToSubtract);

  return {
    isPastCutoffDate: currentDate > cutoffDate,
    cutoffDate,
  };
};

/**
 * Calculate the next delivery dates for a given day of the week after (non-inclusive) a specific startDate.
 * The startDate is not necessarily the same weekday, but rather, the date that the new dates should be after.
 * @param params
 * @param params.startDate The initial delivery date to calculate from (non-inclusive)
 * @param params.targetDayOfWeek Numeric day of week (1 = Monday, ..., 7 = Sunday)
 * @param params.numWeeks Number of weeks to calculate dates for (num items in result array)
 * @returns An array of dates for the next `numWeeks` occurrences of the given day of the week, after startDate
 */

interface CalculateDeliveryDaysAfterParams {
  startDate: Date;
  targetDayOfWeek: number;
  numWeeks: number;
}

export function calculateDeliveryDatesAfter({
  startDate,
  targetDayOfWeek,
  numWeeks,
}: CalculateDeliveryDaysAfterParams): Date[] {
  // Standardize the startDate to HST by adjusting its UTC hours
  const standardizedStartDate = new Date(startDate);
  standardizedStartDate.setUTCHours(HST_TO_UTC_OFFSET, 0, 0, 0);

  const startDateTime = DateTime.fromJSDate(standardizedStartDate);
  const startDayOfWeek = startDateTime.weekday; // Get the numeric value of the startDate's day of the week

  // Calculate the number of days to add to reach the first occurrence of the target day of the week
  let daysToAdd: number;

  if (targetDayOfWeek === startDayOfWeek) {
    // If the target day is the same as the start day, skip to the next week
    daysToAdd = 7;
  } else if (targetDayOfWeek > startDayOfWeek) {
    // Target day is later in the week
    daysToAdd = targetDayOfWeek - startDayOfWeek;
  } else {
    // Target day is earlier in the week
    daysToAdd = 7 - (startDayOfWeek - targetDayOfWeek);
  }

  // Calculate the first delivery date (always after the startDate)
  const firstDeliveryDate = startDateTime.plus({ days: daysToAdd });

  // Generate the next `numWeeks` delivery dates
  const deliveryDates = Array.from({ length: numWeeks }, (_, index) =>
    firstDeliveryDate.plus({ weeks: index }).toJSDate()
  );

  return deliveryDates;
}

/**
 * This needs to be kept in sync with the MealPlanFoodType enum defined in shef-api/src/food-items/FoodItem.graphql
 * in the event that more MealPlanFoodType values are added
 */
const MAIN = 'main';
const SIDE = 'side';
const STAPLE = 'staple';
const LUNCH = 'lunch';
const DRINK = 'drink';
const DESSERT = 'dessert';
const SNACK = 'snack';
const KIDS_MAIN = 'kidsMain';
type MealPlanFoodType =
  | typeof MAIN
  | typeof SIDE
  | typeof STAPLE
  | typeof KIDS_MAIN
  | typeof DRINK
  | typeof LUNCH
  | typeof SNACK
  | typeof DESSERT
  | undefined
  | null;

/**
 * Helper function for simple type-only check (used internally by utility functions)
 */
const isMealPlanSideOrStapleType = (foodType: string | null | undefined): boolean =>
  [SIDE, STAPLE].includes(foodType ?? '');

/**
 * Checks if a food item is a meal plan side dish (side or staple) based on model properties
 * This is the robust version that checks both model properties AND food type
 */
export const isMealPlanSideOrStaple = (foodItem: FoodItem): boolean =>
  Boolean(foodItem.isMainsPlusSides || foodItem.isFlexibleSides) &&
  isMealPlanSideOrStapleType(foodItem?.mealPlanFoodType ?? null);

/**
 * This helper function will only return true for MealPlanFoodType.Side.
 * MainsPlusSides plans don't have any Side type food items, so this will always
 * return false for those plans.
 */
export const isMealPlanSide = (foodType: MealPlanFoodType): boolean => [SIDE].includes(foodType ?? '');

export const getServingsPerMealByFoodType = (mealPlanFoodType: MealPlanFoodType, numServings: number): number => {
  const isSideDish = isMealPlanSideOrStapleType(mealPlanFoodType);
  return isSideDish ? 1 : numServings;
};

/**
 * Helper function for simple type-only check (used internally by utility functions)
 */
const isMealPlanMainType = (foodType: string | null | undefined): boolean => [MAIN].includes(foodType ?? '');

/**
 * Checks if a food item is a meal plan main dish based on model properties
 * This is the robust version that checks both model properties AND food type
 */
export const isMealPlanMain = (foodItem: FoodItem): boolean =>
  Boolean(foodItem.isMainsPlusSides || foodItem.isFlexibleSides) &&
  isMealPlanMainType(foodItem?.mealPlanFoodType ?? null);

export const getSelectedUnitsFromLineItemByFoodType = (
  mealPlanFoodType: MealPlanFoodType,
  lineItemQuantity: number,
  numServings: number
): number => {
  if (isMealPlanSideOrStapleType(mealPlanFoodType)) {
    return lineItemQuantity;
  }

  return lineItemQuantity / numServings;
};

/**
 * This needs to be kept in sync with the MealPlanFormat enum defined in shef-api/src/meal-plan/MealPlan.graphql
 * in the event that we add a new format with new pricing structures
 */

export enum MealPlanFormat {
  FlexibleSides = 'FLEXIBLE_SIDES',
  MainsPlusSides = 'MAINS_PLUS_SIDES',
}

/**
 * Gets the base price for a meal plan side or staple based on model properties
 */
export const getBasePriceForMealPlanSideOrStaple = (
  foodItem: FoodItem,
  formatToCheck?: MealPlanFormat | null
): number => {
  const isMPSSideItem = Boolean(foodItem.isMainsPlusSides) && foodItem.mealPlanFoodType === STAPLE;
  const isFSStapleItem = Boolean(foodItem.isFlexibleSides) && foodItem.mealPlanFoodType === STAPLE;
  const isFSSideItem = Boolean(foodItem.isFlexibleSides) && foodItem.mealPlanFoodType === SIDE;
  const isFlexibleSides = formatToCheck === MealPlanFormat.FlexibleSides;

  return match({ isMPSSideItem, isFSStapleItem, isFSSideItem, isFlexibleSides })
    .with({ isMPSSideItem: true, isFlexibleSides: false }, () => MEAL_PLAN_DEFAULT_SIDE_PRICE)
    .with({ isFSStapleItem: true, isFlexibleSides: true }, () => FLEXIBLE_SIDES_DEFAULT_STAPLE_PRICE)
    .with({ isFSSideItem: true, isFlexibleSides: true }, () => FLEXIBLE_SIDES_DEFAULT_SIDE_PRICE)
    .with({ isMPSSideItem: true, isFlexibleSides: true }, () => FLEXIBLE_SIDES_DEFAULT_SIDE_PRICE)
    .otherwise(() => 0);
};

/**
 * Determines if meal plans are supported for a given region and delivery hub combination.
 * Meal plans require both:
 * 1. The region to be in the list of supported meal plan regions
 * 2. The zip code to have a delivery hub ID (indicating delivery infrastructure)
 *
 * @param regionId - The region ID to check
 * @param deliveryHubId - The delivery hub ID to check (can be null/undefined)
 * @returns true if meal plans are supported, false otherwise
 */
export const supportsMealPlans = (
  regionId: number | null | undefined,
  deliveryHubId: string | number | null | undefined
): boolean => MEAL_PLAN_REGIONS_IDS.includes(regionId ?? 0) && Boolean(deliveryHubId);

/**
 * Legacy alias for backward compatibility
 * @deprecated Use supportsMealPlans instead for clearer naming
 */
export const isInMealPlanRegion = supportsMealPlans;
